import moment from "moment";

/**
 * Parse flexible date formats like dd.mm.yyyy, dd.mm, yyyy, dd, mm
 * Returns object with parsed date information and search patterns
 */
export const parseFlexibleDate = (input) => {
    if (!input || typeof input !== 'string') return null;

    const trimmed = input.trim();
    if (!trimmed) return null;

    const result = {
        original: trimmed,
        patterns: [],
        exactDate: null,
        yearOnly: null,
        monthOnly: null,
        dayOnly: null,
        dateRanges: []
    };

    // Try different date patterns
    const datePatterns = [
        // dd.mm.yyyy or dd/mm/yyyy or dd-mm-yyyy
        { regex: /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/, type: 'full', format: 'DD.MM.YYYY' },
        // dd.mm or dd/mm or dd-mm (assume current year)
        { regex: /^(\d{1,2})[./-](\d{1,2})$/, type: 'dayMonth', format: 'DD.MM' },
        // yyyy only
        { regex: /^(\d{4})$/, type: 'year', format: 'YYYY' },
        // dd only (assume current month/year)
        { regex: /^(\d{1,2})$/, type: 'day', format: 'DD' },
        // mm only as month number (assume current year)
        { regex: /^(0?[1-9]|1[0-2])$/, type: 'month', format: 'MM' }
    ];

    for (const pattern of datePatterns) {
        const match = trimmed.match(pattern.regex);
        if (match) {
            const currentYear = moment().year();
            const currentMonth = moment().month() + 1; // moment months are 0-indexed

            switch (pattern.type) {
                case 'full':
                    const day = parseInt(match[1]);
                    const month = parseInt(match[2]);
                    const year = parseInt(match[3]);
                    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                        const fullDate = moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`);
                        if (fullDate.isValid()) {
                            result.exactDate = fullDate.format('YYYY-MM-DD');
                            result.patterns.push(`${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`);
                        }
                    }
                    break;

                case 'dayMonth':
                    const dayDM = parseInt(match[1]);
                    const monthDM = parseInt(match[2]);
                    if (dayDM >= 1 && dayDM <= 31 && monthDM >= 1 && monthDM <= 12) {
                        const dateWithCurrentYear = moment(`${currentYear}-${monthDM.toString().padStart(2, '0')}-${dayDM.toString().padStart(2, '0')}`);
                        if (dateWithCurrentYear.isValid()) {
                            result.exactDate = dateWithCurrentYear.format('YYYY-MM-DD');
                            result.patterns.push(`${dayDM.toString().padStart(2, '0')}.${monthDM.toString().padStart(2, '0')}.${currentYear}`);
                            result.patterns.push(`${dayDM.toString().padStart(2, '0')}.${monthDM.toString().padStart(2, '0')}`);
                        }
                    }
                    break;

                case 'year':
                    const yearOnly = parseInt(match[1]);
                    if (yearOnly >= 1900 && yearOnly <= 2100) {
                        result.yearOnly = yearOnly;
                        result.dateRanges.push({
                            start: `${yearOnly}-01-01`,
                            end: `${yearOnly}-12-31`
                        });
                        result.patterns.push(yearOnly.toString());
                    }
                    break;

                case 'day':
                    const dayOnly = parseInt(match[1]);
                    if (dayOnly >= 1 && dayOnly <= 31) {
                        result.dayOnly = dayOnly;
                        // Create range for this day in current month/year
                        const dayFormatted = dayOnly.toString().padStart(2, '0');
                        const currentMonthFormatted = currentMonth.toString().padStart(2, '0');
                        const exactDateCurrentMonth = moment(`${currentYear}-${currentMonthFormatted}-${dayFormatted}`);
                        if (exactDateCurrentMonth.isValid()) {
                            result.exactDate = exactDateCurrentMonth.format('YYYY-MM-DD');
                            result.patterns.push(`${dayFormatted}.${currentMonthFormatted}.${currentYear}`);
                            result.patterns.push(dayFormatted);
                        }
                    }
                    break;

                case 'month':
                    const monthOnly = parseInt(match[1]);
                    if (monthOnly >= 1 && monthOnly <= 12) {
                        result.monthOnly = monthOnly;
                        const monthFormatted = monthOnly.toString().padStart(2, '0');
                        result.dateRanges.push({
                            start: `${currentYear}-${monthFormatted}-01`,
                            end: moment(`${currentYear}-${monthFormatted}-01`).endOf('month').format('YYYY-MM-DD')
                        });
                        result.patterns.push(monthFormatted);
                        result.patterns.push(monthOnly.toString());
                    }
                    break;

                default:
                    // No action needed for unknown pattern types
                    break;
            }
            break; // Use first matching pattern
        }
    }

    return result.patterns.length > 0 ? result : null;
};

/**
 * Enhanced search function that handles multiple search criteria
 */
export const buildSearchQuery = (searchTerm, searchableFields = []) => {
    if (!searchTerm || typeof searchTerm !== 'string') {
        return { searchTerms: [], dateSearch: null };
    }

    const terms = searchTerm.trim().split(/\s+/).filter(term => term.length > 0);
    const searchTerms = [];
    let dateSearch = null;

    for (const term of terms) {
        // Try to parse as date first
        const dateParsed = parseFlexibleDate(term);
        if (dateParsed) {
            dateSearch = dateParsed;
        } else {
            // Add as regular search term
            searchTerms.push(term.toLowerCase());
        }
    }

    return { searchTerms, dateSearch };
};

/**
 * Filter reports based on enhanced search criteria
 */
export const filterReports = (reports, searchQuery, additionalFields = {}) => {
    if (!searchQuery || (!searchQuery.searchTerms?.length && !searchQuery.dateSearch)) {
        return reports;
    }

    return reports.filter(report => {
        let matches = true;

        // Date search
        if (searchQuery.dateSearch) {
            const reportDate = moment(report.maintenanceDate || report.createdAt);
            let dateMatches = false;

            if (searchQuery.dateSearch.exactDate) {
                dateMatches = reportDate.format('YYYY-MM-DD') === searchQuery.dateSearch.exactDate;
            } else if (searchQuery.dateSearch.dateRanges?.length > 0) {
                dateMatches = searchQuery.dateSearch.dateRanges.some(range => {
                    return reportDate.isBetween(range.start, range.end, 'day', '[]');
                });
            }

            if (!dateMatches) matches = false;
        }

        // Text search terms
        if (matches && searchQuery.searchTerms?.length > 0) {
            const searchableText = [
                report.excavator?.manufacturer,
                report.excavator?.model,
                report.excavator?.serialNumber,
                report.operator?.name || report.operator,
                report.technician,
                report.type,
                report.notes,
                report._id,
                ...Object.values(additionalFields)
            ].filter(Boolean).join(' ').toLowerCase();

            const termMatches = searchQuery.searchTerms.every(term =>
                searchableText.includes(term)
            );

            if (!termMatches) matches = false;
        }

        return matches;
    });
};

/**
 * Filter Betriebstagebuch entries based on enhanced search criteria
 */
export const filterBetriebstageBuchEntries = (entries, searchQuery, additionalFields = {}) => {
    if (!searchQuery || (!searchQuery.searchTerms?.length && !searchQuery.dateSearch)) {
        return entries;
    }

    return entries.filter(entry => {
        let matches = true;

        // Date search
        if (searchQuery.dateSearch) {
            const entryDate = moment(entry.date || entry.createdAt);
            let dateMatches = false;

            if (searchQuery.dateSearch.exactDate) {
                dateMatches = entryDate.format('YYYY-MM-DD') === searchQuery.dateSearch.exactDate;
            } else if (searchQuery.dateSearch.dateRanges?.length > 0) {
                dateMatches = searchQuery.dateSearch.dateRanges.some(range => {
                    return entryDate.isBetween(range.start, range.end, 'day', '[]');
                });
            }

            if (!dateMatches) matches = false;
        }

        // Text search terms
        if (matches && searchQuery.searchTerms?.length > 0) {
            const searchableText = [
                entry.creator?.name,
                entry.reviewerName,
                entry.generalNotes,
                entry.weather,
                entry.shiftStart,
                entry.shiftEnd,
                entry._id,
                ...Object.values(additionalFields)
            ].filter(Boolean).join(' ').toLowerCase();

            const termMatches = searchQuery.searchTerms.every(term =>
                searchableText.includes(term)
            );

            if (!termMatches) matches = false;
        }

        return matches;
    });
};

/**
 * Get search suggestions for autocomplete
 */
export const getSearchSuggestions = (input, type = 'report') => {
    const suggestions = [];

    if (!input) return suggestions;

    // Date format suggestions
    if (/^\d/.test(input)) {
        suggestions.push(
            { label: 'dd.mm.yyyy (e.g., 15.03.2024)', type: 'format' },
            { label: 'dd.mm (e.g., 15.03)', type: 'format' },
            { label: 'yyyy (e.g., 2024)', type: 'format' },
            { label: 'dd (e.g., 15)', type: 'format' },
            { label: 'mm (e.g., 03)', type: 'format' }
        );
    }

    return suggestions;
};
